import React, { useEffect, useRef, useState } from "react";
import { Row, Col, Form, Select, SelectProps, Tag } from "antd";
import { generateErrorMessage } from "src/utils/error-message";
import FormLabel from "../FormLabel/FormLabel";
import { FormInstance, FormListFieldData } from "antd/es/form";
import PlaceHolderDashed from "../PlaceHolderDashed/PlaceHolderDashed";
import { componentPalette } from "src/theme/component-palette";
import { CloseOutlined } from "@ant-design/icons";
import VerticalCenter from "src/components/WrapCenter/VerticalCenter";
import { BsTrashFill } from "react-icons/bs";
import "./GroupFlareTagPi.css";
import { flareVolumeStore } from "src/stores/flare-volume-store";
import { useShallow } from "zustand/react/shallow";

type TagRender = SelectProps["tagRender"];

interface GrouFlareTagPiProps {
  index: number;
  formSetupFlareVolume: FormInstance;
  name: number;
  restField: Partial<FormListFieldData>;
  optionsFlareInformation: { label: string; value: string }[];
  remove: (name: number | number[]) => void;
  fields: FormListFieldData[];
  errorFlareVolume?: string | null;
}

const GroupFlareTagPi: React.FC<GrouFlareTagPiProps> = ({
  index,
  formSetupFlareVolume,
  name,
  restField,
  optionsFlareInformation,
  remove,
  fields,
  errorFlareVolume,
}) => {
  const selectRef: any = useRef(null);
  const [inputHeight, setInputHeight] = useState("auto");
  const [cnt, setCnt] = useState(0);
  const clearError = flareVolumeStore(useShallow((state) => state.clearError));

  const clearErrorFlareVolume = () => {
    if (errorFlareVolume) clearError("errorFlareVolume");
  };

  // Function to update the height of the input based on the select's height
  const updateInputHeight = () => {
    if (selectRef.current) {
      setInputHeight(selectRef?.current?.nativeElement?.offsetHeight);
    }
  };
  useEffect(() => {
    // Update height initially
    updateInputHeight();
  }, [cnt, selectRef?.current?.nativeElement?.offsetHeight]);

  const sleepReRender = async () => {
    setTimeout(() => {
      setCnt(cnt + 1);
    }, 200);
  };

  useEffect(() => {
    // Add event listener to update height whenever the window is resized
    window.addEventListener("resize", updateInputHeight);
    // Cleanup event listener on component unmount
    sleepReRender();
    return () => {
      window.removeEventListener("resize", updateInputHeight);
    };
  }, []);

  const tagRender: TagRender = (props) => {
    const { label, closable, onClose } = props;
    const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();
    };
    return (
      <Tag
        color={componentPalette.redPeriodDate}
        onMouseDown={onPreventMouseDown}
        closable={closable}
        onClose={onClose}
        closeIcon={<CloseOutlined style={{ color: componentPalette.black }} />}
        style={{ marginInlineEnd: 4, marginBottom: 1.5, marginTop: 1.5 }}
      >
        <span style={{ color: componentPalette.black }}>{label}</span>
      </Tag>
    );
  };

  return (
    <Row className="w-full">
      <Col xs={24} sm={12} lg={12} id={"flare-selector"}>
        <Form.Item
          style={{ marginBottom: 12 }}
          {...restField}
          {...(index === 0 ? { label: <FormLabel label={"Flare Name"} required /> } : {})}
          name={[name, "flareName"]}
          rules={[
            {
              required: true,
              message: generateErrorMessage("Flare Name"),
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                const allFlareNames = getFieldValue("tagPiPortionToFlare").map((item: any) => item?.flareName);
                if (allFlareNames.filter((flareName: string) => flareName === value).length > 1) {
                  return Promise.reject(new Error("Duplicate flare name."));
                }
                return Promise.resolve();
              },
            }),
          ]}
        >
          <Select
            options={optionsFlareInformation}
            className="w-full"
            onChange={async () => {
              // Trigger validation for all flareName fields
              clearErrorFlareVolume();
              await formSetupFlareVolume
                .validateFields(fields.map((field) => ["tagPiPortionToFlare", field.name, "flareName"]))
                .catch(() => console.log());
            }}
            style={{ minHeight: 32, height: inputHeight }}
          />
        </Form.Item>
      </Col>
      <Col xs={24} sm={12} lg={12}>
        <Row id="tag-pi-selector">
          <Form.Item
            style={{ marginBottom: 12, width: "90%" }}
            {...restField}
            name={[name, "tagPi"]}
            {...(index === 0 ? { label: <FormLabel label={"Tag PI"} required /> } : {})}
            rules={[{ required: true, message: generateErrorMessage("Tag PI") }]}
          >
            <Select
              ref={selectRef}
              mode="tags"
              placeholder={<PlaceHolderDashed wording="Add TagPi" />}
              className="w-full"
              onChange={async () => {
                await new Promise((resolve) => setTimeout(resolve, 100));
                setCnt(cnt + 1);
              }}
              tagRender={tagRender}
              options={[]}
            />
          </Form.Item>
          <div className={`pl-1`} style={{ width: "10%", marginTop: index === 0 ? 17.5 : -12.5 }}>
            <VerticalCenter>
              <BsTrashFill
                className="cursor-pointer"
                color={componentPalette.lightgrey}
                size={20}
                onClick={async () => {
                  remove(name);
                  await new Promise((resolve) => setTimeout(resolve, 100));
                  await formSetupFlareVolume
                    .validateFields(
                      fields.map((field) => ["tagPiPortionToFlare", field.name, "flareName"]),
                      { dirty: true },
                    )
                    .catch(() => console.log());
                }}
              />
            </VerticalCenter>
          </div>
        </Row>
      </Col>
    </Row>
  );
};

export default GroupFlareTagPi;
